import React, { useEffect, useState, useCallback } from 'react';
import { History } from 'history';

import { PageLoadingIndicator } from '@spotify-internal/anchor-ds';
import { COLOR_MUTED_BACKGROUND } from 'components/PaywallsShared/constants';
import { AnchorAPI } from 'modules/AnchorAPI';
import { Metadata } from 'types/Metadata';
import { useT } from 'i18n/hooks/useT';
import { Container, Header, Content } from './styles';

const ERROR_ROUTE = '/404';

interface PaywallsSubscriptionDisabledProps {
  history: History;
  match: {
    params: {
      vanitySlug: string;
    };
  };
}

export const PaywallsSubscriptionDisabled = ({
  history,
  match: {
    params: { vanitySlug },
  },
}: PaywallsSubscriptionDisabledProps) => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const forwardTo404 = useCallback(() => history.push(ERROR_ROUTE), [history]);
  const t = useT();

  useEffect(() => {
    const getData = async () => {
      try {
        const stationId = await AnchorAPI.getStationIdForVanityName(vanitySlug);
        const metaResponse = await AnchorAPI.getPodcastMetadata(stationId);
        // I think we can eventually remove this check and the metadata call, but this keeps the URL feature-flagged for now
        if (!metaResponse?.stationPaywall?.isSubscriptionEnabled) {
          return forwardTo404();
        }
        setMetadata(metaResponse);
      } catch (err) {
        forwardTo404();
      }
      return null;
    };
    if (vanitySlug) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vanitySlug]);

  if (!vanitySlug) {
    forwardTo404();
    return null;
  }

  if (!metadata) {
    return <PageLoadingIndicator color={COLOR_MUTED_BACKGROUND} />;
  }

  return (
    <Container>
      <Header>
        {t(
          'PaywallsSubscriptionDisabled.Header',
          'This subscription no longer exists',
          'Subscription disabled title'
        )}
      </Header>
      <Content>
        {t(
          'PaywallsSubscriptionDisabled.Message',
          'The subscription for "{podcastName}" is no longer available.',
          'Subscription disabled message',
          { podcastName: metadata.podcastName }
        )}
      </Content>
    </Container>
  );
};
